import { SxProps, Theme } from '@mui/material/styles';

import { NSPartLocationList } from '../../../../../@types/part.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../@types/partType.type';
import { ManifestData } from '../../../../../@types/shipment.type';
import { CONDITION_ORDER } from '../../../../../constants/shipment';

export const cellSx: SxProps<Theme> = {
  fontSize: '12px',
  py: 0.75,
  px: 1.5,
};

export const headerSx: SxProps<Theme> = {
  ...cellSx,
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '11px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

export const btnSx: SxProps<Theme> = {
  fontSize: '11px',
  textTransform: 'none' as const,
  py: 0.75,
  px: 2,
  bgcolor: 'v4.accent',
  color: 'common.white',
  '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 },
};

export const fieldSx: SxProps<Theme> = {
  fontSize: '12px',
  border: '1px solid',
  borderColor: 'v4.borderDark',
  borderRadius: 1,
  px: 1,
  py: 0.5,
  width: '100%',
};

export const labelSx: SxProps<Theme> = {
  fontSize: '9px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  mb: 0.25,
};

export const formatDate = (d?: string): string => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

export interface ManifestEntry {
  key: string;
  type: 'serialised' | 'non-serialised';
  partTypeName: string;
  partTypeNo: string;
  serial?: string;
  quantity: number;
  // Linkage fields the BFF needs to create real manifest rows (see
  // ManifestData in @types/shipment.type.ts for the POST /shipment contract).
  partId?: number;
  partTypeId?: number;
  nsPartId?: number;
  conditionCode?: string;
  conditionName?: string;
  fleetTags?: string;
  fleetTagId?: number;
  softwareVersionId?: number;
  hardwareRevisionId?: number;
  firmwareVersionId?: number;
  locationId?: number;
  partTypeCategoryCode?: string;
  // Available stock for non-serialised entries — quantity is capped to this.
  maxQuantity?: number;
}

// A consumable part type whose source stock spans multiple conditions — the
// user must pick which NSPart record to dispatch from.
export interface ConditionChoice {
  partTypeId: number;
  partTypeName: string;
  partTypeNo: string;
  rows: NSPartLocationList[];
}

export const buildSerialisedEntry = (item: PartTypeActionLiveSearchItem): ManifestEntry => ({
  key: `part-${item.partId}`,
  type: 'serialised',
  partTypeName: item.partTypeName || item.name || '-',
  partTypeNo: item.partTypeNumber || '-',
  serial: item.serial1 || undefined,
  quantity: 1,
  partId: item.partId,
  conditionCode: item.conditionCode,
  conditionName: item.partCondition?.name,
  locationId: item.locationId,
});

export const buildNSPartEntry = (
  meta: Pick<ConditionChoice, 'partTypeId' | 'partTypeName' | 'partTypeNo'>,
  row: NSPartLocationList
): ManifestEntry => ({
  key: `nspart-${row.id}`,
  type: 'non-serialised',
  partTypeName: meta.partTypeName,
  partTypeNo: meta.partTypeNo,
  quantity: 1,
  partTypeId: meta.partTypeId,
  nsPartId: row.id,
  conditionCode: row.conditionCode,
  conditionName: row.condition,
  fleetTags: row.fleetTagName,
  fleetTagId: row.fleetTagId,
  locationId: row.locationId,
  partTypeCategoryCode: PartTypeActionLiveSearchItemCategoryCode.Consumable,
  maxQuantity: row.quantity,
});

export const sortStockByCondition = (rows: NSPartLocationList[]): NSPartLocationList[] =>
  [...rows].sort((a, b) => {
    const indexA = CONDITION_ORDER.indexOf(a.condition || '');
    const indexB = CONDITION_ORDER.indexOf(b.condition || '');
    return (
      (indexA === -1 ? CONDITION_ORDER.length : indexA) -
      (indexB === -1 ? CONDITION_ORDER.length : indexB)
    );
  });

// Maps manifest entries to the POST /shipment contract. Serialised items must
// carry partId and must NOT carry qty/partTypeId; non-serialised items must
// carry partTypeId + qty + ns_part_id and NOT partId — otherwise the BFF
// silently drops the item and the shipment is created with no parts.
export const buildManifestPayload = (manifest: ManifestEntry[]): ManifestData[] =>
  manifest.map((m) =>
    m.type === 'serialised'
      ? {
          partId: m.partId,
          conditionCode: m.conditionCode,
          fleetTags: m.fleetTags,
          fleetTagId: m.fleetTagId,
          softwareVersion: m.softwareVersionId,
          hardwareRevision: m.hardwareRevisionId,
          firmwareVersion: m.firmwareVersionId,
          locationId: m.locationId,
          selected: true,
        }
      : {
          ns_part_id: m.nsPartId,
          partTypeId: m.partTypeId,
          conditionCode: m.conditionCode,
          fleetTags: m.fleetTags,
          fleetTagId: m.fleetTagId,
          qty: m.quantity,
          locationId: m.locationId,
          partTypeCategoryCode: m.partTypeCategoryCode,
          selected: true,
        }
  );

const escapeCsv = (value: string | number | undefined): string => {
  const str = value == null ? '' : String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export const buildManifestCsv = (manifest: ManifestEntry[]): string => {
  const rows = [
    ['Type', 'Part Type', 'Part No', 'Serial', 'Condition', 'Qty'].map(escapeCsv).join(','),
    ...manifest.map((m) =>
      [m.type, m.partTypeName, m.partTypeNo, m.serial ?? '', m.conditionName ?? '', m.quantity]
        .map(escapeCsv)
        .join(',')
    ),
  ];
  return rows.join('\n');
};
